import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../../agenticsaas/contexts/AuthContext';
import { useOrganizationContext } from '../../agenticsaas/contexts/OrganizationContext';
import { rcmService } from '../../services/rcmService';
import DashboardCommandHeader from './components/DashboardCommandHeader';
import {
  NoDataPanel,
  SectionDataSourceBadge,
} from './components/DashboardDataSourceNotice';
import {
  normalizeReportingPeriod,
  reportingPeriodKey,
} from '../../utils/reportingPeriod';
import { useDashboardReportingPeriod } from './hooks/useDashboardReportingPeriod';
import OperationalHighlightRow from './components/OperationalHighlightRow';
import { useDashboardBranding } from './hooks/useDashboardBranding';
import { useOperationalLayout } from './hooks/useOperationalLayout';
import { 
  AlertTriangle, 
  FileText, 
  DollarSign, 
  Shield, 
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  RefreshCw,
  ArrowRight,
  BarChart3,
  Clock,
  Target,
  Percent,
  CreditCard,
  Globe,
  Zap
} from 'lucide-react';

// Helper to format currency
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
};

// Helper to format percentage
const formatPercent = (value, decimals = 1) => {
  if (value == null || isNaN(value)) return '0.0%';
  return `${Number(value).toFixed(decimals)}%`;
};

// Helper to format number
const formatNumber = (value) => {
  if (value == null || isNaN(value)) return '0';
  return new Intl.NumberFormat('en-US').format(value);
};

export default function OperationalDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, initialized } = useAuthContext();
  const { organizationId: contextOrganizationId, loading: orgContextLoading } = useOrganizationContext();
  const { period, onPeriodChange } = useDashboardReportingPeriod();
  const reportingPeriod = normalizeReportingPeriod(period);
  const { branding, user: brandingUser, organizationId: brandingOrganizationId } = useDashboardBranding({ mode: 'operational' });
  const organizationId = contextOrganizationId || brandingOrganizationId;
  const { enabledSectionIds, highlightTiles } = useOperationalLayout(organizationId);
  const headerUser = brandingUser || user;

  const sectionOrder = useMemo(
    () => Object.fromEntries(enabledSectionIds.map((id, index) => [id, index])),
    [enabledSectionIds]
  );

  const renderSection = useCallback(
    (sectionId, node) => {
      if (!enabledSectionIds.includes(sectionId)) return null;
      return (
        <div key={sectionId} style={{ order: sectionOrder[sectionId] }}>
          {node}
        </div>
      );
    },
    [enabledSectionIds, sectionOrder]
  );
  
  // Log component mount
  useEffect(() => {
    console.log('🔵 [Dashboard] Component mounted');
    console.log('🔵 [Dashboard] Current path:', window.location.pathname);
  }, []);
  
  // Check if we have token and organization_id even if AuthContext hasn't initialized yet
  const hasToken = !!localStorage.getItem('token') || !!localStorage.getItem('auth_token');
  const hasOrgId = !!localStorage.getItem('selectedOrganizationId') || !!localStorage.getItem('organizationId');
  const orgIdFromUser = user?.organization_id || user?.organizationId;
  const hasOrgIdAnywhere = hasOrgId || !!orgIdFromUser;
  
  // Don't wait for auth to fully initialize if we have credentials
  // This allows dashboard to load immediately after login
  // If we have token + orgId, we can start fetching immediately
  // Otherwise, wait for auth to initialize
  const shouldFetch = Boolean(
    initialized &&
    !authLoading &&
    !orgContextLoading &&
    hasToken &&
    organizationId
  );

  // Debug logging
  useEffect(() => {
    console.log('[Dashboard] State:', {
      initialized,
      authLoading,
      orgContextLoading,
      isAuthenticated,
      hasUser: !!user,
      hasToken,
      organizationId,
      shouldFetch
    });
  }, [initialized, authLoading, orgContextLoading, isAuthenticated, user, hasToken, organizationId, shouldFetch]);

  const queryKey = ['dashboard', organizationId, reportingPeriodKey(reportingPeriod)];
  
  const { data, isLoading, refetch, isFetching, error } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        return await rcmService.getDashboardData(organizationId, reportingPeriod);
      } catch (err) {
        // Even if getDashboardData throws, return empty data so dashboard still renders
        console.warn('[Dashboard] getDashboardData failed, using empty data:', err.message);
        return rcmService.getEmptyDashboardData('error');
      }
    },
    enabled: shouldFetch,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 120000, // 2 minutes
    gcTime: 600000, // 10 minutes (renamed from cacheTime in v5)
    retry: 1, // Retry once on failure
    retryDelay: 1000,
    // Don't treat partial failures as errors - always return data
    throwOnError: false,
  });

  // Trigger refetch when shouldFetch becomes true (auth becomes ready)
  // This ensures we fetch immediately when auth is ready, even if React Query doesn't auto-trigger
  useEffect(() => {
    if (shouldFetch && !isFetching && !data && !isLoading) {
      console.log('[Dashboard] shouldFetch became true, triggering initial fetch');
      // Small delay to ensure query is fully enabled
      const timeoutId = setTimeout(() => {
        refetch();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [shouldFetch, isFetching, data, isLoading, refetch]);

  const dashboardData = data || rcmService.getEmptyDashboardData('loading');
  const isInitialLoad = isLoading && !data;
  const dataSources = dashboardData.meta?.sources || {};

  const operationalStatus = useMemo(() => {
    const meta = dashboardData.meta;
    if (!meta || isInitialLoad) {
      return 'Loading RCM service metrics and work queues';
    }
    const parts = [];
    if (meta.allLive) {
      parts.push(`Live data from ${meta.liveCount} RCM sources`);
    } else if (meta.allUnavailable) {
      parts.push('RCM services unavailable — no live metrics loaded');
    } else if (meta.useMock) {
      parts.push(`Demo data for ${meta.mockCount} source${meta.mockCount === 1 ? '' : 's'}`);
      if (meta.liveCount > 0) {
        parts.push(`${meta.liveCount} live`);
      }
    } else {
      parts.push(`${meta.liveCount} live · ${meta.unavailableCount} unavailable`);
    }
    if (dashboardData.loadTime > 0) {
      parts.push(`Loaded in ${dashboardData.loadTime}ms`);
    }
    return parts.join(' · ');
  }, [dashboardData.loadTime, dashboardData.meta, isInitialLoad]);

  const operationalRefreshedAt = useMemo(() => {
    if (!data || isFetching) return null;
    return new Date().toISOString();
  }, [data, isFetching]);

  // Extract Executive Summary KPIs from each service
  const denialKPIs = dashboardData.denials ? {
    totalDenials: dashboardData.denials.denial_summary?.total_denials || 0,
    denialRate: dashboardData.denials.denial_summary?.denial_rate || 0,
    totalDenied: dashboardData.denials.financial_summary?.total_denied || 0,
    totalRecovered: dashboardData.denials.financial_summary?.total_recovered || 0,
    appealSuccessRate: dashboardData.denials.performance_metrics?.appeal_success_rate || 0,
    pendingAppeals: dashboardData.denials.workflow_summary?.pending_appeals || 0,
  } : null;

  const claimsKPIs = dashboardData.claims ? {
    totalProcessed: dashboardData.claims.recent_claims_30_days || dashboardData.claims.processing_summary?.total_processed || 0,
    totalCharged: dashboardData.claims.financial_summary?.total_charged || 0,
    totalPaid: dashboardData.claims.financial_summary?.total_paid || 0,
    successRate: dashboardData.claims.processing_summary?.success_rate
      ?? dashboardData.claims.success_rate
      ?? 0,
    automatedProcessing: dashboardData.claims.processing_summary?.automated_processing
      ?? dashboardData.claims.processing_summary?.ai_processed
      ?? dashboardData.claimsWorkQueue?.assigned_to_ai
      ?? 0,
  } : null;

  const chargeKPIs = dashboardData.charges ? {
    totalCharges: dashboardData.charges.summary?.total_charges || 0,
    totalRevenue: dashboardData.charges.summary?.total_revenue || 0,
    captureRate: dashboardData.charges.summary?.charge_capture_rate || 0,
    averageCharge: dashboardData.charges.summary?.average_charge_amount || 0,
    missingEncounters: dashboardData.charges.summary?.missing_encounters || 0,
  } : null;

  const insuranceKPIs = dashboardData.insurance ? {
    totalRequests: dashboardData.insurance.verification_success_rate?.periods?.['30d']?.total_verifications || 0,
    successRate: dashboardData.insurance.verification_success_rate?.periods?.['30d']?.success_rate || 0,
    verifiedRequests: dashboardData.insurance.verification_success_rate?.periods?.['30d']?.successful_verifications || 0,
    pendingRequests: dashboardData.insurance.work_queue_status?.pending_count || 0,
    totalCollected: dashboardData.insurance.patient_responsibility_collection?.periods?.mtd?.total_collected || 0,
  } : null;

  const priorAuthKPIs = dashboardData.priorAuth ? {
    approvalRate: dashboardData.priorAuth.periods?.['30d']?.approval_rate || 0,
    totalRequests: dashboardData.priorAuth.periods?.['30d']?.total_requests || 0,
    approvedRequests: dashboardData.priorAuth.periods?.['30d']?.approved_requests || 0,
    revenueProtected: dashboardData.priorAuth.periods?.['30d']?.revenue_protected || 0,
    avgDaysToAuth: dashboardData.priorAuth.periods?.['30d']?.average_days_to_authorization || 0,
  } : null;

  // Extract ARPosting KPIs
  const arPostingKPIs = dashboardData.arPosting ? {
    totalPostedToday: dashboardData.arPosting.summary?.total_posted_today || 0,
    autoPostingRate: dashboardData.arPosting.summary?.auto_posting_rate || 0,
    reconciliationRate: dashboardData.arPosting.summary?.reconciliation_rate || 0,
    pendingActions: dashboardData.arPosting.summary?.pending_actions || 0,
    totalPosted: dashboardData.arPosting.summary?.total_posted || 0,
  } : null;

  // Extract ClearingHouse KPIs
  const clearinghouseKPIs = dashboardData.clearinghouse ? {
    totalTransactions: dashboardData.clearinghouse.total_transactions || 0,
    successRate: dashboardData.clearinghouse.success_rate || 0,
    pendingTransactions: dashboardData.clearinghouse.pending_transactions || 0,
    failedTransactions: dashboardData.clearinghouse.failed_transactions || 0,
    successfulTransactions: dashboardData.clearinghouse.successful_transactions || 0,
  } : null;

  // Extract Operational KPIs from Work Queue data
  const denialOperational = dashboardData.denialsWorkQueue ? {
    totalItems: dashboardData.denialsWorkQueue.total_items || 0,
    pendingItems: dashboardData.denialsWorkQueue.pending_count || 0,
    inProgressItems: dashboardData.denialsWorkQueue.in_progress_count || 0,
    completedToday: dashboardData.denialsWorkQueue.completed_today || 0,
    assignedToAI: dashboardData.denialsWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.denialsWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.denialsWorkQueue.overdue_count || 0,
    avgProcessingTime: dashboardData.denialsWorkQueue.avg_processing_time || 0,
  } : null;

  const claimsOperational = dashboardData.claimsWorkQueue ? {
    totalItems: dashboardData.claimsWorkQueue.total_items || 0,
    pendingItems: dashboardData.claimsWorkQueue.pending_count || 0,
    inProgressItems: dashboardData.claimsWorkQueue.in_progress_count || 0,
    completedToday: dashboardData.claimsWorkQueue.completed_today || 0,
    assignedToAI: dashboardData.claimsWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.claimsWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.claimsWorkQueue.overdue_count || 0,
    avgProcessingTime:
      dashboardData.claimsWorkQueue.avg_age_days
      ?? dashboardData.claimsWorkQueue.avg_processing_time
      ?? 0,
  } : null;

  const chargeOperational = dashboardData.chargesWorkQueue ? {
    totalItems: dashboardData.chargesWorkQueue.total_items || 0,
    pendingItems: dashboardData.chargesWorkQueue.pending_count || 0,
    inProgressItems: dashboardData.chargesWorkQueue.in_progress_count || 0,
    completedToday: dashboardData.chargesWorkQueue.completed_today || 0,
    assignedToAI: dashboardData.chargesWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.chargesWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.chargesWorkQueue.overdue_count || 0,
    avgProcessingTime: dashboardData.chargesWorkQueue.avg_processing_time || 0,
  } : null;

  const insuranceOperational = dashboardData.insuranceWorkQueue ? {
    totalItems: dashboardData.insuranceWorkQueue.total_items || 0,
    pendingItems: dashboardData.insuranceWorkQueue.pending_count || 0,
    inProgressItems: dashboardData.insuranceWorkQueue.in_progress_count || 0,
    completedToday: dashboardData.insuranceWorkQueue.completed_today || 0,
    assignedToAI: dashboardData.insuranceWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.insuranceWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.insuranceWorkQueue.overdue_count || 0,
    avgProcessingTime: dashboardData.insuranceWorkQueue.avg_processing_time || 0,
  } : null;

  const priorAuthOperational = dashboardData.priorAuthWorkQueue ? {
    totalItems: dashboardData.priorAuthWorkQueue.total_items || 0,
    pendingItems: dashboardData.priorAuthWorkQueue.pending_count || 0,
    inProgressItems: dashboardData.priorAuthWorkQueue.in_progress_count || 0,
    completedToday: dashboardData.priorAuthWorkQueue.completed_today || 0,
    assignedToAI: dashboardData.priorAuthWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.priorAuthWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.priorAuthWorkQueue.overdue_count || 0,
    avgProcessingTime: dashboardData.priorAuthWorkQueue.avg_processing_time || 0,
  } : null;

  // Extract ARPosting Operational KPIs
  const arPostingOperational = dashboardData.arPostingWorkQueue ? {
    totalItems: dashboardData.arPostingWorkQueue.total_items || 0,
    pendingItems: dashboardData.arPostingWorkQueue.pending_count || dashboardData.arPostingWorkQueue.pending || 0,
    inProgressItems: dashboardData.arPostingWorkQueue.in_progress_count || dashboardData.arPostingWorkQueue.in_progress || 0,
    completedToday: dashboardData.arPostingWorkQueue.completed_today || dashboardData.arPostingWorkQueue.completed || 0,
    assignedToAI: dashboardData.arPostingWorkQueue.assigned_to_ai || 0,
    assignedToHuman: dashboardData.arPostingWorkQueue.assigned_to_human || 0,
    overdueItems: dashboardData.arPostingWorkQueue.overdue_count || dashboardData.arPostingWorkQueue.overdue_items || 0,
    avgProcessingTime: dashboardData.arPostingWorkQueue.avg_processing_time || 0,
  } : null;

  // Always render something, even if loading
  console.log('🔵 [Dashboard] Rendering with state:', {
    isLoading,
    isInitialLoad,
    hasData: !!data,
    error: error?.message,
    shouldFetch,
    dashboardDataKeys: Object.keys(dashboardData || {})
  });

  return (
    <div className="flex flex-col gap-6">
      <DashboardCommandHeader
        mode="operational"
        branding={branding}
        period={reportingPeriod}
        onPeriodChange={onPeriodChange}
        onRefresh={() => refetch()}
        isFetching={isFetching}
        user={headerUser}
        refreshedAt={operationalRefreshedAt}
        statusMessage={operationalStatus}
      />
      {/* Loading State */}
      {isInitialLoad && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
            <p className="text-sm text-gray-700">Loading executive summary KPIs...</p>
          </div>
        </div>
      )}

      {/* Error State - Only show if we have NO data at all (not just partial failures) */}
      {error && !data && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800">Error loading dashboard data</p>
              <p className="text-xs text-red-600 mt-1">{error.message || 'Unknown error occurred'}</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs text-gray-600">
          <p>Debug: initialized={String(initialized)}, shouldFetch={String(shouldFetch)}, hasData={String(!!data)}, isLoading={String(isLoading)}</p>
        </div>
      )}

      <OperationalHighlightRow tiles={highlightTiles} />

      {renderSection('rcm_performance_kpis', (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">RCM Performance KPIs</h2>
                {(dataSources['kpis.daysInAR'] === 'mock'
                  || dataSources['kpis.netCollectionRate'] === 'mock'
                  || dataSources['kpis.automationRate'] === 'mock') && (
                  <SectionDataSourceBadge source="mock" />
                )}
                {dashboardData.kpis
                  && Object.keys(dataSources).some((key) => key.startsWith('kpis.') && dataSources[key] === 'live')
                  && (
                  <SectionDataSourceBadge source="live" />
                )}
              </div>
              <span className="text-sm text-gray-500">Key financial and operational metrics</span>
            </div>
            {!dashboardData.kpis ? (
              <NoDataPanel message="RCM performance KPIs are unavailable. Start AR Posting and related services to load Days in A/R, collection rate, and other metrics." />
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {/* Days in A/R */}
              <div className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4 ${
                dashboardData.kpis.daysInAR?.days_in_ar <= (dashboardData.kpis.daysInAR?.target || 40)
                  ? 'border-l-green-500'
                  : 'border-l-orange-500'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Days in A/R</h3>
                  <Clock className="w-5 h-5 text-gray-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData.kpis.daysInAR?.days_in_ar != null
                    ? dashboardData.kpis.daysInAR.days_in_ar.toFixed(1)
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Average days receivables are outstanding. Target: &lt;{dashboardData.kpis.daysInAR?.target || 40} days</p>
                {dashboardData.kpis.daysInAR?.days_in_ar != null && (
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded ${
                  dashboardData.kpis.daysInAR.days_in_ar <= (dashboardData.kpis.daysInAR?.target || 40)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {dashboardData.kpis.daysInAR.days_in_ar <= (dashboardData.kpis.daysInAR?.target || 40) ? 'On target' : 'Needs attention'}
                </span>
                )}
              </div>

              {/* Net Collection Rate */}
              <div className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4 ${
                dashboardData.kpis.netCollectionRate?.net_collection_rate >= (dashboardData.kpis.netCollectionRate?.target || 95)
                  ? 'border-l-green-500'
                  : 'border-l-orange-500'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Net Collection Rate</h3>
                  <Percent className="w-5 h-5 text-gray-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData.kpis.netCollectionRate?.net_collection_rate != null
                    ? `${dashboardData.kpis.netCollectionRate.net_collection_rate.toFixed(1)}%`
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Percent of expected revenue collected. Target: &gt;{dashboardData.kpis.netCollectionRate?.target || 95}%</p>
                {dashboardData.kpis.netCollectionRate?.net_collection_rate != null && (
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded ${
                  dashboardData.kpis.netCollectionRate.net_collection_rate >= (dashboardData.kpis.netCollectionRate?.target || 95)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {dashboardData.kpis.netCollectionRate.net_collection_rate >= (dashboardData.kpis.netCollectionRate?.target || 95) ? 'On target' : 'Needs attention'}
                </span>
                )}
              </div>

              {/* POS Collections */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">POS Collections</h3>
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData.kpis.posCollections?.pos_collections != null
                    ? formatCurrency(dashboardData.kpis.posCollections.pos_collections)
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Point-of-service collections; {dashboardData.kpis.posCollections?.pos_percentage?.toFixed(1) || '0'}% of total patient payments collected at time of service
                </p>
              </div>

              {/* Propensity to Pay */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4 border-l-purple-500">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Propensity to Pay</h3>
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData.kpis.propensityToPay?.avg_propensity_score != null
                    ? dashboardData.kpis.propensityToPay.avg_propensity_score.toFixed(1)
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Average patient likelihood-to-pay score (0–100). Used for collection prioritization</p>
                {dashboardData.kpis.propensityToPay?.avg_propensity_score != null && (
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded ${
                  dashboardData.kpis.propensityToPay.avg_propensity_score >= 70
                    ? 'bg-green-100 text-green-800'
                    : dashboardData.kpis.propensityToPay.avg_propensity_score >= 50
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {dashboardData.kpis.propensityToPay.avg_propensity_score >= 70 ? 'High' : 
                   dashboardData.kpis.propensityToPay.avg_propensity_score >= 50 ? 'Medium' : 'Low'}
                </span>
                )}
              </div>

              {/* Cost to Collect */}
              <div className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4 ${
                dashboardData.kpis.costToCollect?.cost_to_collect_pct <= (dashboardData.kpis.costToCollect?.target || 3)
                  ? 'border-l-green-500'
                  : 'border-l-orange-500'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Cost to Collect</h3>
                  <DollarSign className="w-5 h-5 text-gray-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData.kpis.costToCollect?.cost_to_collect_pct != null
                    ? `${dashboardData.kpis.costToCollect.cost_to_collect_pct.toFixed(2)}%`
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Cost of collection as % of revenue. Target: &lt;{dashboardData.kpis.costToCollect?.target || 3}%</p>
                {dashboardData.kpis.costToCollect?.cost_to_collect_pct != null && (
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded ${
                  dashboardData.kpis.costToCollect.cost_to_collect_pct <= (dashboardData.kpis.costToCollect?.target || 3)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {dashboardData.kpis.costToCollect.cost_to_collect_pct <= (dashboardData.kpis.costToCollect?.target || 3) ? 'On target' : 'Needs attention'}
                </span>
                )}
              </div>
            </div>

            {/* Automation Rate - Full Width */}
            {dashboardData.kpis.automationRate != null && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Automation Rate</p>
                    <p className="text-xs text-gray-500">Overall RCM process automation</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-3xl font-bold text-blue-600">
                      {dashboardData.kpis.automationRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">AI-assisted processing</p>
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(dashboardData.kpis.automationRate, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            )}
            </>
            )}
          </div>
        </div>
      ))}

      {renderSection('denial_management', (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Denial Management</h2>
              <SectionDataSourceBadge source={dataSources.denials} />
            </div>
            <button
              onClick={() => navigate('/denials')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Denial volume, recovery, and appeal performance</p>
          {denialKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Denials</h3>
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(denialKPIs.totalDenials)}</p>
                <p className="text-xs text-gray-500 mt-2">Denial rate: {formatPercent(denialKPIs.denialRate)} of claims denied in period</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Recovered</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(denialKPIs.totalRecovered)}</p>
                <p className="text-xs text-gray-500 mt-2">Revenue recovered from appeals; out of {formatCurrency(denialKPIs.totalDenied)} total denied</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Appeal Success Rate</h3>
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(denialKPIs.appealSuccessRate)}</p>
                <p className="text-xs text-gray-500 mt-2">{formatNumber(denialKPIs.pendingAppeals)} appeals pending; percent of appeals overturned</p>
              </div>
            </div>
          ) : (
            <NoDataPanel
              message="Denial metrics unavailable. Ensure Denial Management is running and has data for this organization."
              hint={dashboardData.meta?.diagnostics?.denials?.hint}
            />
          )}

          {/* Denial Management Operational KPIs */}
          {denialOperational ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(denialOperational.totalItems)}</p>
                  <p className="text-xs text-gray-500 mt-1">Items in denial work queue</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${denialOperational.pendingItems > 50 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatNumber(denialOperational.pendingItems)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Awaiting assignment or action</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(denialOperational.inProgressItems)}</p>
                  <p className="text-xs text-gray-500 mt-1">Currently being worked</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(denialOperational.completedToday)}</p>
                  <p className="text-xs text-gray-500 mt-1">Resolved in current day</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(denialOperational.assignedToAI)}</p>
                  <p className="text-xs text-gray-500 mt-1">Handled by AI coworker</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(denialOperational.assignedToHuman)}</p>
                  <p className="text-xs text-gray-500 mt-1">Requiring staff review</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(denialOperational.overdueItems)}</p>
                  <p className="text-xs text-gray-500 mt-1">Past due date</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Processing Time</p>
                  <p className="text-xl font-bold text-gray-900">
                    {denialOperational.avgProcessingTime ? `${denialOperational.avgProcessingTime.toFixed(1)}h` : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Average time to resolve</p>
                </div>
              </div>
            </div>
          ) : dataSources.denials === 'live' || dataSources.denials === 'mock' ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <NoDataPanel message="Work queue metrics unavailable from Denial Management." className="py-4" />
            </div>
          ) : null}
        </div>
      </div>
      ))}

      {renderSection('claims_processing', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Claims Processing</h2>
              <SectionDataSourceBadge source={dataSources.claims} />
            </div>
            <button
              onClick={() => navigate('/claims')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Claim submission and adjudication metrics</p>
          {claimsKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Processed (30d)</h3>
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(claimsKPIs.totalProcessed)}</p>
                <p className="text-xs text-gray-500 mt-2">Claims processed in last 30 days; success rate {formatPercent(claimsKPIs.successRate)}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Paid</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(claimsKPIs.totalPaid)}</p>
                <p className="text-xs text-gray-500 mt-2">Amount paid by payers; out of {formatCurrency(claimsKPIs.totalCharged)} total charged</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Automated Processing</h3>
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(claimsKPIs.automatedProcessing)}</p>
                <p className="text-xs text-gray-500 mt-2">Claims handled without manual intervention</p>
              </div>
            </div>
          ) : (
            <NoDataPanel message="Claims metrics unavailable. Ensure Claims Processing is running and has data for this organization." />
          )}

          {/* Claims Processing Operational KPIs */}
          {claimsOperational ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-gray-900">{formatNumber(claimsOperational.totalItems)}</p>
                  <p className="text-xs text-gray-500 mt-1">In claims work queue</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${claimsOperational.pendingItems > 50 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatNumber(claimsOperational.pendingItems)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Awaiting action</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(claimsOperational.inProgressItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(claimsOperational.completedToday)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(claimsOperational.assignedToAI)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(claimsOperational.assignedToHuman)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(claimsOperational.overdueItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Queue Age</p>
                  <p className="text-xl font-bold text-blue-600">
                    {claimsOperational.avgProcessingTime ? `${claimsOperational.avgProcessingTime.toFixed(1)}d` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      ))}

      {renderSection('charge_management', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Charge Management</h2>
              <SectionDataSourceBadge source={dataSources.charges} />
            </div>
            <button
              onClick={() => navigate('/charges')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Revenue from captured charges and capture rate</p>
          {chargeKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(chargeKPIs.totalRevenue)}</p>
                <p className="text-xs text-gray-500 mt-2">From {formatNumber(chargeKPIs.totalCharges)} charges captured</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Capture Rate</h3>
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(chargeKPIs.captureRate)}</p>
                <p className="text-xs text-gray-500 mt-2">Percent of eligible encounters captured; avg {formatCurrency(chargeKPIs.averageCharge)} per charge</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Missing Encounters</h3>
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <p className={`text-3xl font-bold ${chargeKPIs.missingEncounters > 10 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatNumber(chargeKPIs.missingEncounters)}
                </p>
                <p className="text-xs text-gray-500 mt-2">Encounters not yet billed; requires follow-up</p>
              </div>
            </div>
          ) : (
            <NoDataPanel
              message="Charge metrics unavailable. Ensure Charge Capture is running and has data for this organization."
              hint={dashboardData.meta?.diagnostics?.charges?.hint || dashboardData.meta?.diagnostics?.charges?.detail}
            />
          )}

          {/* Charge Management operational KPIs */}
          {chargeOperational && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(chargeOperational.totalItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${chargeOperational.pendingItems > 50 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatNumber(chargeOperational.pendingItems)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(chargeOperational.inProgressItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(chargeOperational.completedToday)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(chargeOperational.assignedToAI)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(chargeOperational.assignedToHuman)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(chargeOperational.overdueItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Processing Time</p>
                  <p className="text-xl font-bold text-blue-600">
                    {chargeOperational.avgProcessingTime ? `${chargeOperational.avgProcessingTime.toFixed(1)}h` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      ))}

      {renderSection('insurance_verification', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Insurance Verification</h2>
              <SectionDataSourceBadge source={dataSources.insurance} />
            </div>
            <button
              onClick={() => navigate('/insurance')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Eligibility verification and patient responsibility</p>
          {insuranceKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Verification Success Rate</h3>
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(insuranceKPIs.successRate)}</p>
                <p className="text-xs text-gray-500 mt-2">{formatNumber(insuranceKPIs.verifiedRequests)} of {formatNumber(insuranceKPIs.totalRequests)} requests verified successfully</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Collected (MTD)</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(insuranceKPIs.totalCollected)}</p>
                <p className="text-xs text-gray-500 mt-2">Patient responsibility collected month to date</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Pending Requests</h3>
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <p className={`text-3xl font-bold ${insuranceKPIs.pendingRequests > 50 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatNumber(insuranceKPIs.pendingRequests)}
                </p>
                <p className="text-xs text-gray-500 mt-2">Verification requests in work queue</p>
              </div>
            </div>
          ) : (
            <NoDataPanel message="Insurance verification metrics unavailable. Ensure the Insurance Verification service is running and has data for this organization." />
          )}

          {/* Insurance Verification Operational KPIs */}
          {insuranceOperational ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(insuranceOperational.totalItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${insuranceOperational.pendingItems > 50 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatNumber(insuranceOperational.pendingItems)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(insuranceOperational.inProgressItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(insuranceOperational.completedToday)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(insuranceOperational.assignedToAI)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(insuranceOperational.assignedToHuman)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(insuranceOperational.overdueItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Processing Time</p>
                  <p className="text-xl font-bold text-blue-600">
                    {insuranceOperational.avgProcessingTime ? `${insuranceOperational.avgProcessingTime.toFixed(1)}h` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      ))}

      {renderSection('prior_authorization', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Prior Authorization</h2>
              <SectionDataSourceBadge source={dataSources.priorAuth} />
            </div>
            <button
              onClick={() => navigate('/prior-auth')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Pre-auth approval and revenue protected</p>
          {priorAuthKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Approval Rate</h3>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(priorAuthKPIs.approvalRate)}</p>
                <p className="text-xs text-gray-500 mt-2">{formatNumber(priorAuthKPIs.approvedRequests)} of {formatNumber(priorAuthKPIs.totalRequests)} requests approved in period</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Revenue Protected</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(priorAuthKPIs.revenueProtected)}</p>
                <p className="text-xs text-gray-500 mt-2">Revenue tied to approved authorizations</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Avg Days to Authorization</h3>
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{priorAuthKPIs.avgDaysToAuth ? `${priorAuthKPIs.avgDaysToAuth.toFixed(1)} days` : 'N/A'}</p>
                <p className="text-xs text-gray-500 mt-2">Average time from request to payer response</p>
              </div>
            </div>
          ) : (
            <NoDataPanel message="Prior authorization metrics unavailable. Ensure the Prior Authorization service is running and has data for this organization." />
          )}

          {/* Prior Authorization Operational KPIs */}
          {priorAuthOperational ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(priorAuthOperational.totalItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${priorAuthOperational.pendingItems > 50 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatNumber(priorAuthOperational.pendingItems)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(priorAuthOperational.inProgressItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(priorAuthOperational.completedToday)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(priorAuthOperational.assignedToAI)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(priorAuthOperational.assignedToHuman)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(priorAuthOperational.overdueItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Processing Time</p>
                  <p className="text-xl font-bold text-blue-600">
                    {priorAuthOperational.avgProcessingTime ? `${priorAuthOperational.avgProcessingTime.toFixed(1)}h` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      ))}

      {renderSection('ar_posting', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">AR Posting</h2>
              <SectionDataSourceBadge source={dataSources.arPosting} />
            </div>
            <button
              onClick={() => navigate('/ar-posting')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Posting and reconciliation of payments</p>
          {arPostingKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Posted Today</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(arPostingKPIs.totalPostedToday)}</p>
                <p className="text-xs text-gray-500 mt-2">Amount posted today; from {formatNumber(arPostingKPIs.totalPosted)} total posted</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Auto-Posting Rate</h3>
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(arPostingKPIs.autoPostingRate)}</p>
                <p className="text-xs text-gray-500 mt-2">Payments posted automatically without manual match</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Reconciliation Rate</h3>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(arPostingKPIs.reconciliationRate)}</p>
                <p className="text-xs text-gray-500 mt-2">Deposits matched to expected remittance</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Pending Actions</h3>
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <p className={`text-3xl font-bold ${arPostingKPIs.pendingActions > 20 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatNumber(arPostingKPIs.pendingActions)}
                </p>
                <p className="text-xs text-gray-500 mt-2">Items requiring manual review or action</p>
              </div>
            </div>
          ) : (
            <NoDataPanel message="AR posting metrics unavailable. Ensure AR Posting is running and has data for this organization." />
          )}

          {/* AR Posting Operational KPIs */}
          {arPostingOperational ? (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-gray-600" />
                Operational Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Total Items</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(arPostingOperational.totalItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pending</p>
                  <p className={`text-xl font-bold ${arPostingOperational.pendingItems > 50 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatNumber(arPostingOperational.pendingItems)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(arPostingOperational.inProgressItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(arPostingOperational.completedToday)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to AI</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(arPostingOperational.assignedToAI)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Assigned to Human</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(arPostingOperational.assignedToHuman)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(arPostingOperational.overdueItems)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Avg Processing Time</p>
                  <p className="text-xl font-bold text-blue-600">
                    {arPostingOperational.avgProcessingTime ? `${arPostingOperational.avgProcessingTime.toFixed(1)}h` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      ))}

      {renderSection('clearinghouse', (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Globe className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">EDI Platform</h2>
              <SectionDataSourceBadge source={dataSources.clearinghouse} />
            </div>
            <button
              onClick={() => navigate('/clearinghouse')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <span>View Details</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">EDI submission and transaction status</p>
          {clearinghouseKPIs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Transactions</h3>
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(clearinghouseKPIs.totalTransactions)}</p>
                <p className="text-xs text-gray-500 mt-2">All EDI transactions submitted through clearinghouse</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Success Rate</h3>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatPercent(clearinghouseKPIs.successRate)}</p>
                <p className="text-xs text-gray-500 mt-2">{formatNumber(clearinghouseKPIs.successfulTransactions)} transactions accepted by payer</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Pending</h3>
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <p className={`text-3xl font-bold ${clearinghouseKPIs.pendingTransactions > 50 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatNumber(clearinghouseKPIs.pendingTransactions)}
                </p>
                <p className="text-xs text-gray-500 mt-2">Transactions in process or awaiting response</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Failed</h3>
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <p className="text-3xl font-bold text-red-600">{formatNumber(clearinghouseKPIs.failedTransactions)}</p>
                <p className="text-xs text-gray-500 mt-2">Rejected or errored; requires correction and resubmission</p>
              </div>
            </div>
          ) : (
            <NoDataPanel
              message="EDI platform metrics unavailable. Ensure the EDI Platform service is running and has data for this organization."
              hint={dashboardData.meta?.diagnostics?.clearinghouse?.hint || dashboardData.meta?.diagnostics?.clearinghouse?.detail}
            />
          )}
        </div>
      </div>
      ))}
    </div>
  );
}
